import React from 'react';
import { A11y, Controller, Pagination } from 'swiper/modules';
import { Swiper, SwiperClass, SwiperSlide } from 'swiper/react';
import { RenderNode } from '@contentful/rich-text-react-renderer';
import { Block, BLOCKS, Inline } from '@contentful/rich-text-types';
import cx from 'classnames';

import { IAppFeatureAnimationItem } from '../../../types/contentful';
import { ScrollReveal } from '../ScrollReveal';
import CtaWidget from '../CtaWidget';
import { DEFAULT_LABEL, DEFAULT_PLACEHOLDER } from '../CtaWidget/shared';
import { AppFeatureAnimationItem } from './AppFeatureAnimationItem';
import RichText from '../RichText';

const disclaimerOverrideOptions: RenderNode = {
  [BLOCKS.PARAGRAPH]: (_node: Block | Inline, children) => {
    return <p>{children}</p>;
  }
};

interface OldDesignProps {
  items: IAppFeatureAnimationItem[];
  ctaWidget: any;
  sysId: string;
  inViewRef: (node?: Element | null) => void;
  inView: boolean;
  itemsState: Record<string, boolean>;
  videoSwiper: SwiperClass | null;
  labelsSwiper: SwiperClass | null;
  selectedItemIndex: number;
  onMediasSwiperInit: (swiper: SwiperClass) => void;
  onLabelsSwiperInit: (swiper: SwiperClass) => void;
  goToIndex: (index: number) => void;
  playItem: (item: IAppFeatureAnimationItem, play: boolean) => void;
  paginationRef: React.RefObject<HTMLDivElement>;
}

export function OldDesign({
  items,
  ctaWidget,
  sysId,
  inViewRef,
  inView,
  itemsState,
  videoSwiper,
  labelsSwiper,
  selectedItemIndex,
  onMediasSwiperInit,
  onLabelsSwiperInit,
  goToIndex,
  playItem,
  paginationRef
}: OldDesignProps) {
  const handleMediasSwiperInit = (swiper: SwiperClass) => {
    const s = swiper as unknown as {
      params: {
        pagination?: {
          el?: HTMLElement | string | null;
          clickable?: boolean;
        };
      };
      pagination?: {
        el?: HTMLElement | null;
        init?: () => void;
        update?: () => void;
        destroy?: () => void;
      };
    };
    if (paginationRef?.current) {
      if (s.pagination?.destroy) s.pagination.destroy();
      s.params.pagination = {
        ...(s.params.pagination || {}),
        el: paginationRef.current,
        clickable: true
      };
      if (s.pagination) s.pagination.el = paginationRef.current;
      if (s.pagination?.init) s.pagination.init();
      if (s.pagination?.update) s.pagination.update();
    }
    onMediasSwiperInit(swiper);
  };
  return (
    <ScrollReveal className="block-container">
      <div
        id={sysId}
        className="flex w-full flex-col-reverse justify-center desktop:flex-row"
        ref={inViewRef}
      >
        <div
          className="mx-3 tablet:mx-12 desktop:ml-auto desktop:w-6/12"
          data-testid="app-feature-animation-medias"
        >
          <Swiper
            className="app-feature-animation-medias tablet:h-[70vh]"
            direction="horizontal"
            modules={[A11y, Pagination, Controller]}
            controller={{ control: labelsSwiper }}
            slidesPerView={1}
            speed={700}
            pagination={{
              el: '.medias-pagination',
              clickable: true
            }}
            uniqueNavElements={false}
            onSwiper={handleMediasSwiperInit}
            breakpoints={{
              1080: {
                spaceBetween: 500,
                direction: 'vertical',
                allowTouchMove: false,
                simulateTouch: false,
                pagination: false
              }
            }}
          >
            {items.map((item) => (
              <SwiperSlide
                key={item.fields?.label}
                className="!flex flex-col items-center justify-center bg-white"
                data-testid="app-feature-animation-media"
              >
                <div className="max-h-[calc(100%-32px)]">
                  <AppFeatureAnimationItem
                    media={item.fields.media}
                    play={inView && itemsState[item.fields.label]}
                    onEnded={() => playItem(item, false)}
                  />
                </div>
                {item.fields.disclaimer && (
                  <div className="mt-4 text-center text-xs leading-3">
                    <RichText
                      data={item.fields.disclaimer}
                      overrideOptions={disclaimerOverrideOptions}
                    />
                  </div>
                )}
              </SwiperSlide>
            ))}
          </Swiper>
          <div
            className="medias-pagination mt-4 flex w-fit justify-center gap-4 py-2 desktop:hidden"
            ref={paginationRef}
          />
          {ctaWidget && (
            <CtaWidget
              id="app-feature-animation-cta"
              module="App Feature Animation"
              height="fit-content"
              width="100%"
              className="app-feature-animation-cta mt-8 desktop:!hidden"
              errorPosition="bottom"
              label={DEFAULT_LABEL}
              placeholder={DEFAULT_PLACEHOLDER}
              {...ctaWidget?.fields}
              sysId={ctaWidget?.sys?.contentType?.sys?.id}
            />
          )}
        </div>
        <div
          className="m-auto mb-8 h-fit w-full overflow-hidden tablet:mb-8 desktop:mb-auto desktop:ml-0 desktop:mr-auto desktop:w-auto"
          data-testid="app-feature-animation-labels"
        >
          <Swiper
            className="app-feature-animation-labels"
            data-testid="app-feature-animation-labels-items"
            slidesPerView="auto"
            direction="horizontal"
            modules={[A11y, Controller]}
            onSwiper={onLabelsSwiperInit}
            controller={{ control: videoSwiper }}
            centeredSlides={true}
            spaceBetween={24}
            breakpoints={{
              720: {
                allowTouchMove: true,
                centeredSlides: true,
                spaceBetween: 24,
                direction: 'horizontal'
              },
              1080: {
                spaceBetween: 24,
                direction: 'vertical',
                allowTouchMove: false,
                centeredSlides: false
              }
            }}
          >
            {items.map((item, index) => (
              <SwiperSlide
                key={item.fields?.label}
                className="!w-fit select-none"
                style={{ marginRight: '16px' }}
                data-testid="app-feature-animation-label"
                tabIndex={-1}
              >
                <button
                  type="button"
                  className={cx(
                    'relative w-fit cursor-pointer text-[32px] font-medium leading-8 outline-none transition-colors duration-200 ease-in-out tablet:text-[48px] tablet:leading-[46px] desktop:py-1',
                    selectedItemIndex === index ? '' : 'text-[#B4B6B9]',
                    'desktop:hover:text-teal'
                  )}
                  style={
                    selectedItemIndex === index
                      ? {
                          color: 'black'
                        }
                      : {}
                  }
                  tabIndex={0}
                  onFocus={() => goToIndex(index)}
                  onClick={() => goToIndex(index)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      goToIndex(index);
                    }
                  }}
                  aria-selected={selectedItemIndex === index}
                  role="tab"
                >
                  {item.fields?.label}
                </button>
              </SwiperSlide>
            ))}
          </Swiper>
          {ctaWidget && (
            <CtaWidget
              id="app-feature-animation-cta"
              module="App Feature Animation"
              height="fit-content"
              className="app-feature-animation-cta mt-8 !hidden desktop:!flex"
              errorPosition="bottom"
              label={DEFAULT_LABEL}
              placeholder={DEFAULT_PLACEHOLDER}
              {...ctaWidget?.fields}
              sysId={ctaWidget?.sys?.contentType?.sys?.id}
            />
          )}
        </div>
      </div>
    </ScrollReveal>
  );
}
